import { colors } from "@activecollab/components";
import { intersection } from "lodash";
import moment from "moment";
import { RRule, RRuleSet } from "rrule";
import i18n from "~/i18n";

export const EditOptionsTypes = {
  ONLY_THIS: "only_this_event",
  THIS_AND_FOLLOWING: "this_and_following_events",
  ALL: "all_events",
};

const REQUIRED_ATTRIBUTES = {
  [EditOptionsTypes.ONLY_THIS]: [
    "starts_on",
    "ends_on",
    "starts_on_time",
    "ends_on_time",
    "recurrence_instance",
    "reference_timezone",
  ],
  [EditOptionsTypes.THIS_AND_FOLLOWING]: [
    "starts_on",
    "ends_on",
    "starts_on_time",
    "ends_on_time",
    "recurrence_instance",
    "reference_timezone",
  ],
  [EditOptionsTypes.ALL]: [],
  edit_repeatig_event: [],
};

const CRITICAL_FIELDS = {
  starts_on: {
    options: [EditOptionsTypes.ONLY_THIS, EditOptionsTypes.THIS_AND_FOLLOWING],
  },
  ends_on: {
    options: [EditOptionsTypes.ONLY_THIS, EditOptionsTypes.THIS_AND_FOLLOWING],
  },
  starts_on_time: {
    options: [EditOptionsTypes.ONLY_THIS, EditOptionsTypes.THIS_AND_FOLLOWING, EditOptionsTypes.ALL],
  },
  ends_on_time: {
    options: [EditOptionsTypes.ONLY_THIS, EditOptionsTypes.THIS_AND_FOLLOWING, EditOptionsTypes.ALL],
  },
  repeat_event: {
    options: [EditOptionsTypes.THIS_AND_FOLLOWING, EditOptionsTypes.ALL],
  },
};

const ATTRIBUTES_TO_REMOVE = {
  [EditOptionsTypes.ONLY_THIS]: ["calendar_id"],
  [EditOptionsTypes.THIS_AND_FOLLOWING]: ["calendar_id"],
  [EditOptionsTypes.ALL]: [],
  edit_repeatig_event: [...Object.keys(CRITICAL_FIELDS), "recurrence_instance", "reference_timezone"],
};

export const ActionTypes = {
  DROP: "drop",
  RESIZE: "resize",
};

export const isRepeating = (event) => {
  return event?.repeat_event && event.repeat_event !== "dont";
};

export const makeRRulefromEvent = (event) => {
  if (isRepeating(event)) {
    let freq = "";
    let interval = 1;
    switch (event.repeat_event) {
      case "daily": {
        freq = RRule.DAILY;
        break;
      }
      case "weekly": {
        freq = RRule.WEEKLY;
        break;
      }
      case "biweekly": {
        freq = RRule.WEEKLY;
        interval = 2;
        break;
      }
      case "monthly": {
        freq = RRule.MONTHLY;
        break;
      }
      case "yearly": {
        freq = RRule.YEARLY;
        break;
      }
    }
    let rrule = new RRule({
      freq,
      interval,
    });
    if (event?.repeat_until) {
      rrule = new RRule({
        freq,
        interval,
        until: moment.unix(event.repeat_until).utc().toDate(),
      });
    }
    return rrule.toString().replace("RRULE:", "");
  }
  return null;
};

export const isAllDay = (event) => {
  return event.starts_on_time === null && event.ends_on_time === null;
};

const getEventDuration = (event, unitOfTime = "seconds") => {
  const dateStrStart = event.starts_on_time
    ? `${event.starts_on} ${event.starts_on_time}`
    : `${event.starts_on} 00:00:00`;
  const dateStrEnd = event.ends_on_time ? `${event.ends_on} ${event.ends_on_time}` : `${event.ends_on} 23:59:59`;

  const start = moment(dateStrStart);
  const end = moment(dateStrEnd);

  return end.diff(start, unitOfTime);
};

export const resolveAllDayOccurrences = (event, after, before, excludedDates = [], exact = null) => {
  const rangeStart = moment.utc(`${after} 00:00:00`, "YYYY-MM-DD HH:mm:ss");
  const rangeEnd = moment.utc(`${before} 23:59:59`, "YYYY-MM-DD HH:mm:ss");

  const duration = getEventDuration(event, "days");
  rangeStart.add(-1 * duration, "days");
  rangeStart.add(-1, "second");
  rangeEnd.add(1, "second");

  const dtStart = `TZID=UTC:${`${event.starts_on.replaceAll("-", "")}T000000Z`}`;

  const rule = RRule.fromString(
    `DTSTART;${dtStart};\n
  RRULE:${event.rrule}`
  );

  const rruleSet = new RRuleSet();
  rruleSet.options.noCache = true;
  rruleSet.rrule(rule);

  if (excludedDates.length > 0) {
    excludedDates.forEach((exDate) => {
      rruleSet.exdate(exDate);
    });
  }

  const occurrences = rruleSet.between(rangeStart.toDate(), rangeEnd.toDate());

  const result = [];

  occurrences.forEach((occurrence) => {
    if ((exact !== null && moment(occurrence).unix() === Number(exact)) || exact === null) {
      result.push({
        start: moment(moment(occurrence).utc().format("YYYY-MM-DD HH:mm:ss")).toDate(),
        end: moment(moment(occurrence).add(duration, "days").utc().format("YYYY-MM-DD HH:mm:ss")).endOf("day").toDate(),
        starts_on_utc: moment(occurrence).unix(),
        ends_on_utc: moment(occurrence).add(duration, "days").unix(),
        starts_on: moment(occurrence).unix(),
        ends_on: moment(occurrence).add(duration, "days").unix(),
      });
    }
  });

  return exact ? (result.length > 0 ? result[0] : null) : result;
};

const handleExDatesDST = (event, exDate, startOn = null) => {
  let differenceFromUTC = 0;
  if (event.starts_on_time && event.ends_on_time) {
    const resourceStartsOnWithReferenceTimezone = moment
      .utc(`${startOn ?? event.starts_on} ${event.starts_on_time}`)
      .tz(event.reference_timezone);

    const dateWithReferenceTimezone = moment(exDate).tz(event.reference_timezone);
    if (resourceStartsOnWithReferenceTimezone?.isDST() && !dateWithReferenceTimezone.isDST()) {
      differenceFromUTC = -1;
    }
    if (!resourceStartsOnWithReferenceTimezone?.isDST() && dateWithReferenceTimezone.isDST()) {
      differenceFromUTC = 1;
    }
  }

  return differenceFromUTC;
};

export const resolveHourlyOccurrences = (event, after, before, excludedDates = [], exact = null) => {
  const rangeStart = moment(`${after} 00:00:00`, "YYYY-MM-DD HH:mm:ss").utc();
  const rangeEnd = moment(`${before} 23:59:59`, "YYYY-MM-DD HH:mm:ss").utc();

  const duration = getEventDuration(event);
  rangeStart.add(-1 * duration, "seconds");
  rangeStart.add(-1, "second");
  rangeEnd.add(1, "second");

  const dtStart = `TZID=UTC:${event.starts_on.replaceAll("-", "")}T${event.starts_on_time.replaceAll(":", "")}`;

  const rule = RRule.fromString(
    `DTSTART;${dtStart};\n
  RRULE:${event.rrule}`
  );

  if (event.repeat_until) {
    // console.log("UNTIL: ", moment.utc(`${event.repeat_until} 00:00:00`, "YYYY-MM-DD HH:mm:ss").endOf("day").format());
    rule.options.until = moment.utc(`${event.repeat_until} 00:00:00`, "YYYY-MM-DD HH:mm:ss").endOf("day").toDate();
  }

  const rruleSet = new RRuleSet(true);
  rruleSet.options.noCache = true;
  rruleSet.rrule(rule);

  if (excludedDates.length > 0) {
    excludedDates.forEach((exDate) => {
      let _date = moment(exDate);
      _date = _date.clone().add(handleExDatesDST(event, exDate), "hours").toDate();
      rruleSet.exdate(_date);
    });
  }

  const occurrences = rruleSet.between(rangeStart.toDate(), rangeEnd.toDate());

  const result = [];

  let reference_timezone = "UTC";

  if (event?.reference_timezone) {
    reference_timezone = event.reference_timezone;
  }

  const resourceStartsOnWithReferenceTimezone = moment
    .utc(`${event.starts_on} ${event.starts_on_time}`)
    .tz(reference_timezone);

  occurrences.forEach((occurrence) => {
    const start = moment(occurrence);
    const end = moment(occurrence).add(duration, "seconds");
    let differenceFromUTC = 0;

    const dateWithReferenceTimezone = moment(occurrence).tz(reference_timezone);

    if (resourceStartsOnWithReferenceTimezone?.isDST() && !dateWithReferenceTimezone.isDST()) {
      differenceFromUTC = 1;
    }
    if (!resourceStartsOnWithReferenceTimezone?.isDST() && dateWithReferenceTimezone.isDST()) {
      differenceFromUTC = -1;
    }

    start.add(differenceFromUTC, "hour");
    end.add(differenceFromUTC, "hour");

    const startUtc = start.clone().utc();

    if ((exact !== null && startUtc.unix() === Number(exact)) || exact === null) {
      result.push({
        start: start.toDate(),
        end: end.add(-1, "second").toDate(),
        starts_on_utc: startUtc.unix(),
        ends_on_utc: moment(occurrence).add(duration, "seconds").unix(),
        starts_on: moment(occurrence).add(differenceFromUTC, "hour").unix(),
        ends_on: moment(occurrence).add(duration, "seconds").add(differenceFromUTC, "hour").unix(),
      });
    }
  });

  return exact ? (result.length > 0 ? result[0] : null) : result;
};

export const getOccurrences = (event, from, to, excludedDates = [], exact = null) => {
  if (isAllDay(event)) {
    return resolveAllDayOccurrences(event, from, to, excludedDates, exact);
  } else {
    return resolveHourlyOccurrences(event, from, to, excludedDates, exact);
  }
};

export const handleRepeatEvents = (selectedDate, event, excludedDates = [], weekType = "week") => {
  let from, to;

  if (typeof selectedDate === "object" && selectedDate.from && selectedDate.to) {
    from = selectedDate.from;
    to = selectedDate.to;
  } else {
    from = moment(selectedDate).startOf("month").startOf(weekType).startOf("day");
    to = moment(selectedDate).endOf("month").endOf(weekType).endOf("day");
  }

  const allDay = isAllDay(event);

  const occurrences = getOccurrences(event, from.format("YYYY-MM-DD"), to.format("YYYY-MM-DD"), excludedDates);

  const events = occurrences.map((data) => {
    return {
      id: event.id,
      title: null,
      name: event.name,
      start: data.start,
      end: data.end,
      allDay: allDay,
      color: event.color,
      repeat: event.repeat_event,
      created_by_id: event.created_by_id,
      calendar_id: event.calendar_id,
      class: event.class,
      starts_on_utc: data.starts_on_utc,
      ends_on_utc: data.ends_on_utc,
      starts_on_time: event.starts_on_time,
      ends_on_time: event.ends_on_time,
      reference_timezone: event.reference_timezone,
      recurrence_instance: null,
      is_one_ocurrence: false,
      is_participant: event.is_participant,
      calendar_name: event.calendar_name,
    };
  });

  return events;
};

export const isOneOcurrence = (event) => {
  if (event.repeat_event === "dont") {
    return true;
  }
  if (event.recurrence_instance !== null) {
    return true;
  }
  if (event.recurrence_instance === null) {
    if (event.repeat_until) {
      const until =
        typeof event.repeat_until === "number"
          ? moment.unix(event.repeat_until)
          : moment.utc(event.repeat_until, "YYYY-MM-DD");
      const startsOn =
        typeof event.starts_on === "number"
          ? moment.unix(event.starts_on)
          : moment.utc(
              isAllDay(event) ? `${event.starts_on} 00:00:00` : `${event.starts_on} ${event.starts_on_time}`,
              "YYYY-MM-DD HH:mm:ss"
            );
      return until.unix() < startsOn.unix();
    }
    return false;
  }
};

export const handleEvents = (events = [], selectedDate, weekType = "week", timezone = "UTC") => {
  return events.reduce((prev, event) => {
    if (!isOneOcurrence(event)) {
      const excludedDates = event?.exceptions
        ? event.exceptions.map((timestamp) =>
            isAllDay(event) ? moment.unix(timestamp).utc().toDate() : moment.unix(timestamp).tz(timezone).toDate()
          )
        : [];

      const repeatedEvents = handleRepeatEvents(selectedDate, event, excludedDates, weekType);
      repeatedEvents.forEach((e) => {
        prev.push(e);
      });
    } else {
      const allDay = isAllDay(event);
      const dateStrStart = event.starts_on_time
        ? `${event.starts_on} ${event.starts_on_time}`
        : `${event.starts_on} 00:00:00`;
      const dateStrEnd = event.ends_on_time ? `${event.ends_on} ${event.ends_on_time}` : `${event.ends_on} 23:59:59`;

      let startsOn = moment(dateStrStart);
      let endsOn = moment(dateStrEnd);

      if (!allDay) {
        startsOn = moment.utc(dateStrStart);
        endsOn = moment.utc(dateStrEnd);
        endsOn.add(-1, "second");
      }

      let result = {
        id: event.id,
        title: null,
        name: event.name,
        start: startsOn.toDate(),
        end: endsOn.toDate(),
        allDay: allDay,
        color: event.color,
        repeat: event.repeat_event,
        created_by_id: event.created_by_id,
        calendar_id: event.calendar_id,
        class: event.class,
        starts_on_utc: allDay
          ? moment.utc(`${startsOn.format("YYYY-MM-DD")} 00:00:00`).unix()
          : startsOn.clone().unix(),
        ends_on_utc: allDay ? moment.utc(`${endsOn.format("YYYY-MM-DD")} 00:00:00`).unix() : endsOn.clone().unix(),
        starts_on_time: event.starts_on_time,
        ends_on_time: event.ends_on_time,
        recurrence_instance: event.recurrence_instance,
        reference_timezone: event.reference_timezone,
        is_one_ocurrence: true,
        is_participant: event.is_participant,
        calendar_name: event.calendar_name,
      };

      if (event?.pending) {
        result = {
          ...result,
          pending: true,
        };
      }
      prev.push(result);
    }
    return prev;
  }, []);
};

export const makeEventForDate = (event, timezone, timestamp = null) => {
  if (timestamp !== null && (isNaN(Number(timestamp)) || !moment.unix(timestamp).isValid())) {
    return null;
  }

  if (isOneOcurrence(event) || timestamp === null) {
    return event;
  }

  const excludedDates = event?.exceptions
    ? event.exceptions.map((timestamp) =>
        isAllDay(event) ? moment.unix(timestamp).utc().toDate() : moment.unix(timestamp).tz(timezone).toDate()
      )
    : [];

  const dateParam = moment.unix(timestamp);

  const startOn = moment.unix(event.starts_on).utc().format("YYYY-MM-DD");
  const endsOn = moment.unix(event.ends_on).utc().format("YYYY-MM-DD");

  const until = event.repeat_until ? moment.unix(event.repeat_until).utc().format("YYYY-MM-DD") : null;

  const data = {
    ...event,
    starts_on: startOn,
    ends_on: endsOn,
    repeat_until: until,
  };

  const from = dateParam.clone().add(-2, "day");
  const to = dateParam.clone().add(2, "day");

  const occurrence = getOccurrences(data, from.format("YYYY-MM-DD"), to.format("YYYY-MM-DD"), excludedDates, timestamp);

  if (occurrence) {
    return {
      ...event,
      starts_on: occurrence.starts_on,
      ends_on: occurrence.ends_on,
    };
  }

  return null;
};

// export const getLastOccurrence = (event, timezone) => {
//   if (isOneOcurrence(event)) {
//     return moment.unix(event.starts_on).toDate();
//   }

//   const excludedDates = event?.exceptions
//     ? event.exceptions.map((timestamp) => {
//         return isAllDay(event) ? moment.unix(timestamp).utc().toDate() : moment.unix(timestamp).tz(timezone).toDate();
//       })
//     : [];

//   const startOn = moment.unix(event.starts_on).utc().format("YYYY-MM-DD");

//   const until = event.repeat_until ? moment.unix(event.repeat_until).utc().endOf("day") : null;

//   const dtStart = isAllDay(event)
//     ? `TZID=UTC:${startOn.replaceAll("-", "")}T000000`
//     : `TZID=UTC:${startOn.replaceAll("-", "")}T${event.starts_on_time.replaceAll(":", "")}`;

//   const rule = RRule.fromString(
//     `DTSTART;${dtStart};\n
//   RRULE:${event.rrule}`
//   );

//   if (event.repeat_until) {
//     rule.options.until = until.toDate();
//   }

//   const rruleSet = new RRuleSet(true);
//   rruleSet.options.noCache = true;
//   rruleSet.rrule(rule);

//   if (excludedDates.length > 0) {
//     excludedDates.forEach((exDate) => {
//       let _date = moment(exDate);
//       _date = _date.clone().add(handleExDatesDST(event, exDate, startOn), "hours").toDate();
//       rruleSet.exdate(_date);
//     });
//   }
//   const rangeStart = moment().add(30, "days").startOf("day").utc();
//   if (isAllDay(event)) {
//     rangeStart.add(-1, "second");
//   }

//   let occurrence = rruleSet.after(rangeStart.toDate());

//   if (!occurrence) {
//     occurrence = rruleSet.before(rangeStart.toDate(), true);
//   }

//   if (!isAllDay(event)) {
//     let reference_timezone = "UTC";

//     if (event?.reference_timezone) {
//       reference_timezone = event.reference_timezone;
//     }

//     const resourceStartsOnWithReferenceTimezone = moment
//       .utc(`${startOn} ${event.starts_on_time}`)
//       .tz(reference_timezone);

//     const testDate = moment(occurrence);
//     let differenceFromUTC = 0;

//     const dateWithReferenceTimezone = moment(occurrence).tz(reference_timezone);

//     if (resourceStartsOnWithReferenceTimezone?.isDST() && !dateWithReferenceTimezone.isDST()) {
//       differenceFromUTC = 1;
//     }
//     if (!resourceStartsOnWithReferenceTimezone?.isDST() && dateWithReferenceTimezone.isDST()) {
//       differenceFromUTC = -1;
//     }
//     testDate.add(differenceFromUTC, "hour");
//     occurrence = testDate.toDate();
//   }

//   return occurrence;
// };

export const getClosestOccurrence = (event, timezone, date = null, onlyAfterDate = false) => {
  if (isOneOcurrence(event)) {
    return moment.unix(event.starts_on).toDate();
  }

  const excludedDates = event?.exceptions
    ? event.exceptions.map((timestamp) => {
        return isAllDay(event) ? moment.unix(timestamp).utc().toDate() : moment.unix(timestamp).tz(timezone).toDate();
      })
    : [];

  const startOn = moment.unix(event.starts_on).utc().format("YYYY-MM-DD");

  const until = event.repeat_until ? moment.unix(event.repeat_until).utc().endOf("day") : null;

  const dtStart = isAllDay(event)
    ? `TZID=UTC:${startOn.replaceAll("-", "")}T000000`
    : `TZID=UTC:${startOn.replaceAll("-", "")}T${event.starts_on_time.replaceAll(":", "")}`;

  const rule = RRule.fromString(
    `DTSTART;${dtStart};\n
  RRULE:${event.rrule}`
  );

  if (event.repeat_until) {
    rule.options.until = until.toDate();
  }

  const rruleSet = new RRuleSet(true);
  rruleSet.options.noCache = true;
  rruleSet.rrule(rule);

  if (excludedDates.length > 0) {
    excludedDates.forEach((exDate) => {
      let _date = moment(exDate);
      _date = _date.clone().add(handleExDatesDST(event, exDate, startOn), "hours").toDate();
      rruleSet.exdate(_date);
    });
  }
  const rangeStart = date ? moment(`${date}`, "YYYY-MM-DD HH:mm:ss") : moment().add(30, "days").startOf("day").utc();
  if (isAllDay(event)) {
    rangeStart.add(-1, "second");
  }

  let occurrence = rruleSet.after(rangeStart.toDate());

  if (!occurrence && !onlyAfterDate) {
    occurrence = rruleSet.before(rangeStart.toDate(), true);
  }

  if (!occurrence) {
    return null;
  }

  if (!isAllDay(event)) {
    let reference_timezone = "UTC";

    if (event?.reference_timezone) {
      reference_timezone = event.reference_timezone;
    }

    const resourceStartsOnWithReferenceTimezone = moment
      .utc(`${startOn} ${event.starts_on_time}`)
      .tz(reference_timezone);

    const testDate = moment(occurrence);
    let differenceFromUTC = 0;

    const dateWithReferenceTimezone = moment(occurrence).tz(reference_timezone);

    if (resourceStartsOnWithReferenceTimezone?.isDST() && !dateWithReferenceTimezone.isDST()) {
      differenceFromUTC = 1;
    }
    if (!resourceStartsOnWithReferenceTimezone?.isDST() && dateWithReferenceTimezone.isDST()) {
      differenceFromUTC = -1;
    }
    testDate.add(differenceFromUTC, "hour");
    occurrence = testDate.toDate();
  }

  return occurrence;
};

// export const getNextOccurrence = (event, from, timezone) => {
//   if (isOneOcurrence(event)) {
//     return moment.unix(event.starts_on).toDate();
//   }

//   const excludedDates = event?.exceptions
//     ? event.exceptions.map((timestamp) => {
//         return isAllDay(event) ? moment.unix(timestamp).utc().toDate() : moment.unix(timestamp).tz(timezone).toDate();
//       })
//     : [];

//   const startOn = moment.unix(event.starts_on).utc().format("YYYY-MM-DD");

//   const until = event.repeat_until ? moment.unix(event.repeat_until).utc().endOf("day") : null;

//   const dtStart = isAllDay(event)
//     ? `TZID=UTC:${startOn.replaceAll("-", "")}T000000`
//     : `TZID=UTC:${startOn.replaceAll("-", "")}T${event.starts_on_time.replaceAll(":", "")}`;

//   const rule = RRule.fromString(
//     `DTSTART;${dtStart};\n
//   RRULE:${event.rrule}`
//   );

//   if (event.repeat_until) {
//     rule.options.until = until.toDate();
//   }

//   const rruleSet = new RRuleSet(true);
//   rruleSet.options.noCache = true;
//   rruleSet.rrule(rule);

//   if (excludedDates.length > 0) {
//     excludedDates.forEach((exDate) => {
//       let _date = moment(exDate);
//       _date = _date.clone().add(handleExDatesDST(event, exDate, startOn), "hours").toDate();
//       rruleSet.exdate(_date);
//     });
//   }

//   const rangeStart = moment(`${from}`, "YYYY-MM-DD HH:mm:ss");
//   if (isAllDay(event)) {
//     rangeStart.startOf("day").add(-1, "second");
//   }

//   let occurrence = rruleSet.after(rangeStart.toDate());

//   if (!occurrence) {
//     return null;
//   }

//   if (!isAllDay(event)) {
//     let reference_timezone = "UTC";

//     if (event?.reference_timezone) {
//       reference_timezone = event.reference_timezone;
//     }

//     const resourceStartsOnWithReferenceTimezone = moment
//       .utc(`${startOn} ${event.starts_on_time}`)
//       .tz(reference_timezone);

//     const testDate = moment(occurrence);
//     let differenceFromUTC = 0;

//     const dateWithReferenceTimezone = moment(occurrence).tz(reference_timezone);

//     if (resourceStartsOnWithReferenceTimezone?.isDST() && !dateWithReferenceTimezone.isDST()) {
//       differenceFromUTC = 1;
//     }
//     if (!resourceStartsOnWithReferenceTimezone?.isDST() && dateWithReferenceTimezone.isDST()) {
//       differenceFromUTC = -1;
//     }
//     testDate.add(differenceFromUTC, "hour");
//     occurrence = testDate.toDate();
//   }

//   return occurrence;
// };

export const onEventUpdate = (event, start, end, actionType, isAllDay, timezone) => {
  let startsOn = moment(start);
  let endsOn = moment(end);

  let startsOnTime = null;
  let endsOnTime = null;

  if (!isAllDay) {
    if (actionType === ActionTypes.DROP) {
      startsOnTime = moment(start);
      endsOn = moment(end).add(1, "second");
      endsOnTime = endsOn.clone();
    }

    if (actionType === ActionTypes.RESIZE) {
      if (!startsOn.isSame(moment(event.start), "minutes")) {
        endsOn.add(1, "second");
      }
      if (startsOn.isSame(endsOn, "minutes")) {
        endsOn.add(5, "minutes");
      }
      startsOnTime = startsOn.clone();
      endsOnTime = endsOn.clone();
    }
  }

  const oldStart = moment(event.start).format();
  const oldEnd = moment(event.end).format();
  const newStart = moment(start).format();
  const newEnd = moment(end).format();

  let attributes = {};

  if (
    !moment(oldStart).isSame(moment(newStart)) ||
    !moment(oldEnd).isSame(moment(newEnd)) ||
    event.allDay !== isAllDay
  ) {
    attributes = {
      allDay: isAllDay,
      reference_timezone: isAllDay ? "UTC" : timezone,
      starts_on: startsOn.format("YYYY-MM-DD"),
      ends_on: endsOn.format("YYYY-MM-DD"),
      starts_on_time: startsOnTime ? startsOnTime.format("HH:mm:ss") : null,
      ends_on_time: endsOnTime ? endsOnTime.format("HH:mm:ss") : null,
    };
  }

  return {
    calendar_id: event.calendar_id,
    id: event.id,
    attributes,
  };
};

const colorMap = {
  //v7colors
  "#63A7DE": colors.blueSky.color,
  "#15CAA1": "#64CD93",
  "#E85287": "#ED6161",
  "#31BFFF": colors.blueSky.color,
  "#A38670": colors.beige.color,
  "#F56F72": colors.coral.color,
  "#FF79AA": colors.pink.color,
  "#D575B6": colors.purple.color,
  "#333333": colors.graySilver.color,
  "#00B25C": colors.greenPale.color,
  "#E2AE00": "#FFCC6B",
  "#177AB0": colors.blueAqua.color,
  "#777777": colors.gray.color,
  "#8F4775": colors.purpleDirty.color,
  "#FB7C0D": colors.orange.color,
  "#021CC9": colors.purple.color,
  "#309AE8": colors.bluePale.color,
  "#E64745": "#ED6161",
  "#5A9721": colors.greenOliveDrab.color,
  "#00ABA2": colors.turquoise.color,
  "#CF6D2E": colors.orange.color,
  "#165F9F": colors.blueSky.color,
  "#E24272": colors.coral.color,
  "#9CAD01": colors.yellow.color,
  //next-colors
  [colors.blueSky.color]: colors.blueSky.color,
  "#64CD93": "#64CD93",
  "#ED6161": "#ED6161",
  [colors.beige.color]: colors.beige.color,
  [colors.coral.color]: colors.coral.color,
  [colors.pink.color]: colors.pink.color,
  [colors.purple.color]: colors.purple.color,
  [colors.graySilver.color]: colors.graySilver.color,
  [colors.greenPale.color]: colors.greenPale.color,
  "#FFCC6B": "#FFCC6B",
  [colors.blueAqua.color]: colors.blueAqua.color,
  [colors.gray.color]: colors.gray.color,
  [colors.purpleDirty.color]: colors.purpleDirty.color,
  [colors.orange.color]: colors.orange.color,
  [colors.bluePale.color]: colors.bluePale.color,
  [colors.greenOliveDrab.color]: colors.greenOliveDrab.color,
  [colors.turquoise.color]: colors.turquoise.color,
  [colors.yellow.color]: colors.yellow.color,
};

export const getCalendarNextColor = (color) => {
  const remap = colorMap[color];
  if (remap) {
    return remap;
  }
  return colors.blueSky.color;
};

export const getGroupCalendarMembersDisplayName = (name = "", users = [], long = false) => {
  if (name?.length) {
    return name;
  }

  if (!Array.isArray(users)) {
    throw new TypeError(`users expected array, received: ${typeof users}`);
  }

  if (users.length === 0) {
    return "";
  }

  if (long) {
    return users.map((user) => user?.short_display_name).join(", ");
  }

  const firstUserName = users[0]?.short_display_name;
  const secondUserName = users[1]?.short_display_name;

  if (users.length === 1) {
    return firstUserName;
  } else if (users.length === 2) {
    return firstUserName + i18n.t(" and ") + secondUserName;
  } else if (users.length === 3) {
    const thirdUserName = users[2]?.short_display_name;
    return firstUserName + ", " + secondUserName + i18n.t(" and ") + thirdUserName;
  }

  return i18n.t("{{firstUserName}}, {{secondUserName}} and {{number}} more", {
    firstUserName,
    secondUserName,
    number: users.length - 2,
  });
};

export const getCalendarServiceName = (calendar) => {
  if (calendar.class === "ExternalCalendar") {
    switch (calendar?.service) {
      case "google":
        return "Google Calendar";
      case "apple":
        return "Apple Calendar";
      case "outlook":
        return "Outlook Calendar";
      default:
        return i18n.t("External Calendar");
    }
  } else {
    return i18n.t("Private Calendar");
  }
};

export const resolveEditOptions = (event, data) => {
  if (!isRepeating(event)) {
    return [];
  }

  const changedCriticalFields = Object.keys(CRITICAL_FIELDS).reduce((prev, field) => {
    if (typeof event[field] !== "undefined" && typeof data[field] !== "undefined") {
      if (event[field] !== data[field]) {
        prev.push(field);
      }
    }
    return prev;
  }, []);

  if (changedCriticalFields.length === 0) {
    return [];
  }

  return changedCriticalFields.reduce(
    (prev, field) => {
      prev = intersection(prev, CRITICAL_FIELDS[field].options);
      return prev;
    },
    [EditOptionsTypes.ONLY_THIS, EditOptionsTypes.THIS_AND_FOLLOWING, EditOptionsTypes.ALL]
  );
};

export const prepareAttributes = (action, event, data) => {
  const requiredAttributes = REQUIRED_ATTRIBUTES[action];

  let attributes = Object.fromEntries(
    Object.entries(data).filter(([key]) => {
      if (Object.keys(CRITICAL_FIELDS).includes(key)) {
        return requiredAttributes.includes(key);
      } else {
        return true;
      }
    })
  );

  requiredAttributes.forEach((key) => {
    if (!Object.keys(attributes).includes(key)) {
      attributes = {
        ...attributes,
        [key]: event[key],
      };
    }
  });

  Object.keys(CRITICAL_FIELDS).forEach((field) => {
    if (typeof event[field] !== "undefined" && typeof data[field] !== "undefined") {
      if (event[field] !== data[field] && CRITICAL_FIELDS[field].options.includes(action)) {
        attributes = {
          ...attributes,
          [field]: data[field],
        };
      }
    }
  });

  const attributesToRemove = ATTRIBUTES_TO_REMOVE[action];

  attributes = Object.fromEntries(
    Object.entries(attributes).filter(([key]) => {
      return !attributesToRemove.includes(key);
    })
  );

  return attributes;
};
